import {
  cerrarSesion,
  esAdmin,
  esSoloLab,
  esSoloOperadorFiori,
  obtenerUsuarioActual,
  puedeConfigurar,
  puedeLab,
  puedeMapearRecetas,
  puedeRegistrarFiori,
} from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { ChevronDown, LogOut, Menu, RefreshCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogoAdelante } from './LogoAdelante';

const linkBase = 'text-sm transition-colors';
const linkInactive = 'text-zinc-400 hover:text-white';
const linkActive = 'text-white';

interface ItemNav {
  to: string;
  label: string;
  end?: boolean;
}

/** Un link suelto o un grupo desplegable de links relacionados. */
type EntradaNav = ItemNav | { grupo: string; items: ItemNav[] };

export function HeaderAdelante() {
  const usuario = obtenerUsuarioActual();
  const iniciales = usuario?.iniciales ?? '?';
  const nombreCompleto = usuario?.name ?? 'Sin sesión';
  const username = usuario?.username ?? '';
  const mostrarAdmin = esAdmin();
  const mostrarLab = puedeLab();
  const mostrarConfig = puedeConfigurar();
  const mostrarRegistrarFiori = puedeRegistrarFiori();
  const mostrarMapeoRecetas = puedeMapearRecetas();
  // Usuario que solo tiene rol Laboratorio (no Admin/Operador/Ingeniería):
  // simplifica el header a un único link "Laboratorio" para que sea fácil
  // de usar desde el celular.
  const soloLab = esSoloLab();
  // Usuario que solo tiene rol OperadorFiori (choferes de las
  // autocargables): header mínimo, solo "Registrar" + "Mis Registros" —
  // el backend acota `/coladas` a lo que él mismo registró.
  const soloOperadorFiori = esSoloOperadorFiori();

  const [menuMovil, setMenuMovil] = useState(false);
  const location = useLocation();
  // Cerrar el menú móvil al navegar a otra ruta.
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo queremos cerrar en cambio de ruta
  useEffect(() => setMenuMovil(false), [location.pathname]);

  const grupoLaboratorio: ItemNav[] = [
    { to: '/lab/muestras', label: 'Laboratorio General' },
    { to: '/lab/esclerometro', label: 'Esclerómetro' },
  ];
  const grupoConfiguracion: ItemNav[] = [
    ...(mostrarAdmin ? [{ to: '/admin/usuarios', label: 'Usuarios' }] : []),
    ...(mostrarAdmin ? [{ to: '/admin/actividades', label: 'Actividades' }] : []),
    ...(mostrarConfig ? [{ to: '/admin/umbrales', label: 'Umbrales' }] : []),
    ...(mostrarConfig ? [{ to: '/admin/densidades', label: 'Densidades' }] : []),
    ...(mostrarAdmin ? [{ to: '/admin/lab-pin', label: 'PIN Lab' }] : []),
    ...(mostrarAdmin ? [{ to: '/admin/fiori-operadores', label: 'Operadores Fiori' }] : []),
  ];

  const entradas: EntradaNav[] = soloOperadorFiori
    ? [
        { to: '/coladas/registrar-fiori', label: 'Registrar' },
        { to: '/coladas', label: 'Mis Registros' },
      ]
    : soloLab
      ? [
          { to: '/lab/muestras', label: 'Muestras' },
          { to: '/lab/esclerometro', label: 'Esclerómetro' },
        ]
      : [
          { to: '/', label: 'Inicio', end: true },
          { to: '/coladas', label: 'Coladas' },
          ...(mostrarRegistrarFiori
            ? [{ to: '/coladas/registrar-fiori', label: 'Registrar Fiori' }]
            : []),
          { to: '/analisis-batches', label: 'Batches' },
          ...(mostrarLab ? [{ grupo: 'Laboratorio', items: grupoLaboratorio } as const] : []),
          ...(grupoConfiguracion.length > 0
            ? [{ grupo: 'Configuración', items: grupoConfiguracion } as const]
            : []),
          ...(mostrarConfig ? [{ to: '/admin/pedidos-bc', label: 'Autorizar BC' }] : []),
          ...(mostrarMapeoRecetas ? [{ to: '/admin/mapeo-recetas', label: 'Mapeo Recetas' }] : []),
        ];

  return (
    <header className="bg-[#0A0A0A] text-white">
      <div className="container mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        {/* Marca */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <LogoAdelante altura={20} />
          <span className="text-sm font-bold tracking-wider">ADELANTE.</span>
        </div>

        {/* Nav desktop */}
        <nav className="hidden items-center gap-6 md:flex">
          {entradas.map((e) =>
            'grupo' in e ? (
              <GrupoDesplegable key={e.grupo} grupo={e.grupo} items={e.items} />
            ) : (
              <NavLink
                key={e.to}
                to={e.to}
                end={e.end}
                className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
              >
                {e.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* Hamburguesa solo en mobile */}
          <button
            type="button"
            onClick={() => setMenuMovil((v) => !v)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-300 hover:bg-white/10 md:hidden"
            aria-label={menuMovil ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuMovil}
          >
            {menuMovil ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <MenuUsuario
            iniciales={iniciales}
            nombreCompleto={nombreCompleto}
            username={username}
            roles={usuario?.roles ?? []}
          />
        </div>
      </div>

      {/* Panel de nav móvil */}
      {menuMovil && (
        <nav className="border-t border-white/10 bg-[#0A0A0A] md:hidden">
          <div className="container mx-auto flex max-w-7xl flex-col px-4 py-2 sm:px-6">
            {entradas.map((e) =>
              'grupo' in e ? (
                <div key={e.grupo} className="py-1.5">
                  <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                    {e.grupo}
                  </div>
                  {e.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMenuMovil(false)}
                      className={({ isActive }) =>
                        cn(
                          'block rounded-md px-3 py-2.5 text-sm transition-colors',
                          isActive ? 'bg-white/10 text-white' : 'text-zinc-300 hover:bg-white/5',
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={e.to}
                  to={e.to}
                  end={e.end}
                  onClick={() => setMenuMovil(false)}
                  className={({ isActive }) =>
                    cn(
                      'rounded-md px-3 py-2.5 text-sm transition-colors',
                      isActive ? 'bg-white/10 text-white' : 'text-zinc-300 hover:bg-white/5',
                    )
                  }
                >
                  {e.label}
                </NavLink>
              ),
            )}
          </div>
        </nav>
      )}
    </header>
  );
}

/**
 * Grupo de links relacionados colapsado bajo un solo item del nav desktop
 * (ej. "Laboratorio" → Laboratorio General / Esclerómetro). Se resalta como
 * activo si la ruta actual coincide con alguno de sus items.
 */
function GrupoDesplegable(props: { grupo: string; items: ItemNav[] }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const activo = props.items.some((i) => location.pathname.startsWith(i.to));

  useEffect(() => {
    if (!abierto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierto]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={cn(linkBase, 'flex items-center gap-1', activo ? linkActive : linkInactive)}
        aria-expanded={abierto}
      >
        {props.grupo}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', abierto && 'rotate-180')} />
      </button>

      {abierto && (
        <div className="absolute left-0 top-8 z-50 min-w-[180px] overflow-hidden rounded-md border border-zinc-200 bg-white py-1 text-zinc-900 shadow-lg">
          {props.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setAbierto(false)}
              className={({ isActive }) =>
                cn(
                  'block px-3 py-2 text-sm hover:bg-zinc-50',
                  isActive && 'bg-zinc-100 font-medium',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Avatar clickeable con dropdown: info de usuario + "Recargar token" +
 * "Cerrar sesión". El menú se cierra al clickear fuera o presionar Escape.
 *
 * "Recargar token" hace un `window.location.reload()` que en muchos casos
 * dispara el flujo silencioso de MSAL y resuelve los `timed_out` ocasionales
 * sin perder la sesión completa. Si el problema persiste, "Cerrar sesión"
 * limpia todo el cache y redirige a login de Microsoft.
 */
function MenuUsuario(props: {
  iniciales: string;
  nombreCompleto: string;
  username: string;
  roles: string[];
}) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Cerrar al clickear fuera o presionar Escape.
  useEffect(() => {
    if (!abierto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierto]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-adelante-primary text-xs font-bold text-adelante-foreground transition hover:ring-2 hover:ring-white/30"
        aria-label={`Menú de usuario: ${props.nombreCompleto}`}
        aria-expanded={abierto}
        title={props.nombreCompleto}
      >
        {props.iniciales}
      </button>

      {abierto && (
        <div className="absolute right-0 top-10 z-50 w-64 overflow-hidden rounded-md border border-zinc-200 bg-white text-zinc-900 shadow-lg">
          <div className="border-b border-zinc-100 p-3">
            <div className="text-sm font-semibold">{props.nombreCompleto}</div>
            {props.username && (
              <div className="mt-0.5 truncate text-xs text-zinc-500">{props.username}</div>
            )}
            {props.roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {props.roles.map((r) => (
                  <span
                    key={r}
                    className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-700"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50"
          >
            <RefreshCw className="h-4 w-4 text-zinc-600" />
            Recargar token
            <span className="ml-auto text-[10px] text-zinc-400">si la app se cuelga</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setAbierto(false);
              cerrarSesion();
            }}
            className="flex w-full items-center gap-2 border-t border-zinc-100 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
